
import shippingService from '@api/routes/cart/getshippinginformation';
import selectAddressService from '@api/routes/cart/selectoraddaddress';
import selectMethodService from '@api/routes/cart/addshippingmethod';
import uniqid from 'uniqid';
import ReactiveComponent, { computed, watch } from "@utils/reactive-component";
import parseRenderingContext from '@utils/parse-rendering-context';
import isFn from '@utils/is-function';
import AjaxLoader from '../ajax-loader/ajax-loader';
import SelectBox from '../select-box/select-box';
import CheckoutAddressForm, { DEFAULT_OPTIONS as addressFormDefaults } from './checkout-address-form';
import AddressValidation, { logUpsServiceError, showSuggestions, statusCodes } from '../address-validation/address-validation';
import template from './templates/checkout-shipping';
import { context as defaultContext } from './checkout-defaults';
import store from '@stores/cart/cart-store';

const CLASS_NAME_ADD_BUTTON_DISABLED = '-disabled';
const CLASS_NAME_EDIT_BUTTON_DISABLED = '-disabled';
const CLASS_NAME_NEXT_BUTTON_DISABLED = 'disabled';

const NEW_ADDRESS_OPTION_VALUE = '__checkoutShipping_newAddress__';

const DEFAULT_OPTIONS = {
    checkoutFormClassList: ''
};

const DEFAULT_LOADER_OPTIONS = {
    aria: false,
    className: 'd-flex align-items-center h-100',
    size: 'xs',
    visible: true
};

export default class CheckoutShipping extends ReactiveComponent {
    #isEdit = false;

    constructor($originEl, context, options) {
        context = { ...defaultContext, ...context };
        options = { ...DEFAULT_OPTIONS, ...options };

        super();

        const $parsed = this.parseTemplate();
        $originEl.replaceWith($parsed);

        this.$el = $parsed;

        this.$isSubscriptionsPage = $(".js-sub-section").length > 0 ;

        this.$addLabel = this.$el.find('[data-label="add"]');
        this.$addressLabel = this.$el.find('[data-label="address"]');
        this.$editLabel = this.$el.find('[data-label="edit"]');
        this.$methodLabel = this.$el.find('[data-label="method"]');
        this.$nextLabel = this.$el.find('[data-label="next"]');

        this.$address = this.$el.find('[data-region="address"]');
        this.$edit = this.$el.find('[data-region="edit"]');
        this.$error = this.$el.find('[data-region="error"]');
        this.$form = this.$el.find('[data-region="form"]');
        this.$hiddenOnForm = this.$el.find('[data-region="hidden-on-form"]');
        this.$controls = this.$el.find('[data-region="controls"]');
        this.$method = this.$el.find('[data-region="method"]');

        this.$addCtrl = this.$el.find('[data-control="add"]');
        this.$editCtrl = this.$el.find('[data-control="edit"]');
        this.$nextCtrl = this.$el.find('[data-control="next"]');

        this.$addressPlaceholder = this.$el.find('[data-placeholder="address"]');
        this.$addressLoaderPlaceholder = this.$el.find('[data-placeholder="addressLoader"]');
        this.$methodPlaceholder = this.$el.find('[data-placeholder="method"]');
        this.$methodLoaderPlaceholder = this.$el.find('[data-placeholder="methodLoader"]');
        this.$nextLoaderPlaceholder = this.$el.find('[data-placeholder="nextLoader"]');
        this.$formPlaceholder = this.$el.find('[data-placeholder="form"]');
        this.$prescription = $('.cart-landing__rx[data-region="prescription"]');

        this.$promotionsnote = this.$el.find('[data-region="promotionsnote"]');

        this.addressLoader = null;
        this.addressSelector = null;
        this.methodLoader = null;
        this.methodSelector = null;
        this.nextLoader = null;

        this.checkoutForm = null;
        this.checkoutFormClassList = options.checkoutFormClassList;

        this.id = uniqid.time('');
        this.addressId = `shippingAddress_${this.id}`;
        this.methodId = `shippingMethod_${this.id}`;

        this.prevAddressValue = '';
        this.disableServiceUpdate = false;
        this.newAddress = {};

        this.reactive({
            addressOptions: [],
            addresses: [],
            selectedAddressValue: '',

            methodOptions: [],
            methods: [],
            selectedMethodValue: '',

            loading: true,
            nextLoading: false,

            formVisible: false
        });

        this.loaders = [];
        this.context = context;
    }

    [computed]() {
        return {
            isNewAddress() {
                return this.selectedAddressValue === NEW_ADDRESS_OPTION_VALUE;
            },
            selectedAddressInfo() {
                if (this.isNewAddress) {
                    return this.newAddress;
                }

                return this.addresses.find(address => address.AddressName === this.selectedAddressValue) || {};
            }
        }
    }

    [watch]() {
        return {
            loading(newValue) {
                this.addressLoader.active = newValue;
                this.methodLoader.active = newValue;
            },
            addressOptions(newOptions) {
                if (!newOptions || !Array.isArray(newOptions) || newOptions.length === 0) {
                    this.addressSelector.disable();
                    this.addressSelector.disableElement();
                    //console.error('invalid address options');

                }

                const options = {
                    options: newOptions,
                    unselectedText: this.context.Select
                };

                this.addressSelector.setOptions(options);
                this.addressSelector.enable();
            },
            methodOptions(newOptions) {
                if ((!newOptions || !Array.isArray(newOptions) || newOptions.length === 0) && !this.$isSubscriptionsPage) {
                    this.methodSelector.disable();
                    this.methodSelector.disableElement();
                    console.error('invalid method options');
                }

                const options = {
                    options: newOptions,
                    unselectedText: this.context.Select
                };

                this.methodSelector.setOptions(options);
                this.methodSelector.enable();
            },
            formVisible() {
                if (this.formVisible) {
                    if (!this.checkoutForm.rendered) {
                        this.checkoutForm.init();
                        this.checkoutForm.render();
                    }

                    this.$form.slideDown('fast');
                    this.$hiddenOnForm.slideUp('fast');
                    this.checkoutForm.focus();
                    this.$editCtrl.attr('aria-expanded', true);
                    this.$addCtrl.attr('aria-expanded', true);
                } else {
                    this.$form.slideUp('fast');
                    this.$hiddenOnForm.slideDown('fast');
                    this.addressSelector.focus();
                    this.$editCtrl.attr('aria-expanded', false);
                    this.$addCtrl.attr('aria-expanded', false);

                    this.#isEdit = false;
                }
            }
        }
    }

    init() {
        this.renderLabels();
        this.renderCheckoutForm();
        this.renderNextCtrl();
        this.renderSelectors();
        this.renderLoaders();
        this.setupAccessibility();
        this.setupListeners();
        this.getShippingData();
    }

    onCartLoaded() {
        this.loaders.forEach(loader => (loader.active = false));
        this.displayLegalDisclaimer();
    }

    async getShippingData() {
        const { data, error, success } = await shippingService.get();

        if (success) {
            this.updateUiOnly(() => {
                this.setupAddresses(data);
                this.setupMethods(data);
            });

            this.updateSummary(data);
        } else {
            this.$error.show().html(error);
        }

        this.loading = false;
    }

    async selectAddress() {
        this.$error.hide();

        const payload = {
            AddressId: this.selectedAddressValue,
            IsSubscriptionPage: this.$isSubscriptionsPage
        };

        const { success, data, error } = await selectAddressService.post(payload);

        if (success) {
            this.updateUiOnly(() => this.setupMethods(data));
            if(!this.$isSubscriptionsPage){
                this.updateSummary(data);
            }
        } else if (error) {
            this.$error.show().html(error);
        }
    }

    async addOrEditAddress(addressParty = {}) {
        const payload = {
            AddressId: !this.isNewAddress ? this.selectedAddressValue : null,
            VcaAddressParty: addressParty,
            IsSubscriptionPage: this.$isSubscriptionsPage
        };

        const { success, data, error } = await selectAddressService.post(payload);

        if (success) {
            const {
                isValidated,
                response: validatedResponse
            } = await this.handleAddressValidation(data.UpsAddressValidationStatus, addressParty, data.SuggestedAddresses);

            if (isValidated) {
                this.updateUiOnly(() => {
                    this.setupAddresses(validatedResponse.data || data);
                    this.setupMethods(validatedResponse.data || data);
                });
                if(!this.$isSubscriptionsPage){
                    this.updateSummary(validatedResponse.data || data);
                }
                this.$editCtrl.prop('disabled', false);
                this.$addCtrl.prop('disabled', false);
                this.formVisible = false;
            }
        } else if (error) {
            this.checkoutForm.showError(error);
        }
    }

    async handleAddressValidation(status, originalAddress, suggestedAddresses = []) {
        const showPrompt = showSuggestions(status);

        if (!showPrompt) {
            if (status === statusCodes.UPS_UNAVAILABLE) logUpsServiceError();

            return {
                isValidated: true,
                response: {}
            }
        };

        return await this.showSuggestedAddresses(status, originalAddress, suggestedAddresses);
    }

    async showSuggestedAddresses(status, originalAddress, suggestedAddresses = []) {
        let response = {};

        const currentAddress = {
            address: originalAddress.Address1,
            address2: originalAddress.Address2,
            city: originalAddress.City,
            stateCode: originalAddress.StateCode,
            zip: originalAddress.ZipPostalCode
        };

        const suggestions = suggestedAddresses?.map((address, index) => ({
            addressId: `${index}`,
            address: address.Address1,
            address2: address.Address2,
            city: address.City,
            stateCode: address.StateCode,
            zip: address.ZipPostalCode
        }));

        const onSubmitHandler = async (isOriginal, value) => {
            let addressParty = originalAddress;

            if (!isOriginal) {
                const index = suggestions.findIndex(({ addressId }) => addressId === value);
                if (index > -1) {
                    addressParty = {
                        ...addressParty,
                        ...suggestedAddresses[index]
                    };
                }
            }

            response = await selectAddressService.post({
                AddressId: !this.isNewAddress ? this.selectedAddressValue : null,
                VcaAddressParty: addressParty,
                IsSuggestion: true,
                IsSubscriptionPage: this.$isSubscriptionsPage
            });

            return response;
        };

        const addressValidation = new AddressValidation(
            status,
            currentAddress,
            suggestions,
            this.context,
            {},
            onSubmitHandler
        );

        const { isValidated } = await addressValidation.prompt();
        addressValidation.destroy();

        return {
            isValidated,
            response
        };
    }

    async postShippingMethod() {
        if (!this.disableServiceUpdate) {
            this.$error.hide();

            store.mutations.SET_LOADING(true);

            const payload = {
                AddressId: this.selectedAddressValue,
                ShippingMethodId: this.selectedMethodValue
            };

            const { data, error, success } = await selectMethodService.post(payload);

            if (success) {
                this.updateSummary(data);
            } else {
                this.$error.show().html(error);
            }

            if (data.ShippingMethodId !== this.selectedMethodValue) {
                this.updateUiOnly(() => {
                    this.resetMethods();
                    this.setupMethods(data);
                });
            }
        }
    }

    setupAddresses(data) {
        const addresses = data.ShippingAddresses || [];
        const shippingParty = addresses.find((address) => address.AddressName === data.AddressId);
        let isPrimaryIndex = data.ShippingAddresses.findIndex(address => address.IsPrimary == true );
        addresses.unshift(addresses.splice(isPrimaryIndex, 1)[0]);

        store.mutations.SET_SHIPPING_PARTY(shippingParty);
        if(addresses[0] != undefined) {
            this.mapAddressOptions(addresses);
            this.setInitialAddress();
        }else{
            this.$edit.hide();
            this.onAddAddressClick();
        }
    }

    mapAddressOptions(addresses = []) {
        this.addresses = addresses;

        const addressOptions = addresses.map((address, index) => {
            if(address === null || address === undefined){
                return;
            }
            const text = this.parseAddressText(address);
            return {
                text,
                value: address.AddressName
            };
        });

        this.addressOptions = addressOptions;
    }

    setAddressLoadingState(loading = true) {
        store.mutations.SET_LOADING(loading);
        this.methodLoader.active = loading;
    }

    setupMethods(data) {
        const methods = data.ShippingOptionsModel?.Shipments?.[0]?.ShippingMethods || []; // this schema is nonsense
        const shippingMethod = methods.find((method) => method.ShippingMethodId === data.ShippingMethodId);

        store.mutations.SET_SHIPPING_METHOD(shippingMethod);
        this.mapMethodOptions(methods);

        this.setInitialMethod();
    }

    mapMethodOptions(methods) {
        this.methods = methods;

        const methodOptions = methods.map(method => {
            return {
                text: method.MethodName,
                value: method.ShippingMethodId
            };
        });

        this.methodOptions = methodOptions;
    }

    resetMethods() {
        this.methodSelector.setOptions({
            options: [{ text: '', value: '' }]
        });

        this.methodSelector.disable();
        this.methodSelector.disableElement();
    }

    onAddAddressClick() {
        if (store.state.loading) return;

        this.formVisible = true;

        this.checkoutForm.setHeading(this.context.AddShippingAddress);

        this.checkoutForm.setValues({
            firstName: this.newAddress.FirstName || '',
            lastName: this.newAddress.LastName || '',
            address: this.newAddress.Address1 || '',
            address2: this.newAddress.Address2 || '',
            city: this.newAddress.City || '',
            state: this.newAddress.State || '',
            zip: this.newAddress.ZipPostalCode || ''
        });

        const options = [...this.addressOptions];
        const newAddressOption = { text: this.context.NewAddress, value: NEW_ADDRESS_OPTION_VALUE };
        const newAddressIndex = options.findIndex(({ value }) => value === NEW_ADDRESS_OPTION_VALUE);

        if (newAddressIndex > -1) {
            options[newAddressIndex] = newAddressOption;
        } else {
            options.push(newAddressOption);
        }

        this.addressOptions = options;

        this.updateUiOnly(() => {
            this.addressSelector.setValue(NEW_ADDRESS_OPTION_VALUE);
        });

        this.$editCtrl.prop('disabled', true);
        this.addressSelector.disableElement();
    }

    onEditClick() {
        if (store.state.loading) return;

        if (this.selectedAddressValue) {
            this.#isEdit = true;

            this.formVisible = true;

            this.checkoutForm.setHeading(this.context.EditShippingAddress);

            this.checkoutForm.setValues({
                firstName: this.selectedAddressInfo.FirstName,
                lastName: this.selectedAddressInfo.LastName,
                address: this.selectedAddressInfo.Address1,
                address2: this.selectedAddressInfo.Address2,
                city: this.selectedAddressInfo.City,
                state: this.selectedAddressInfo.StateCode,
                zip: this.selectedAddressInfo.ZipPostalCode
            });

            this.$addCtrl.prop('disabled', true);
            this.addressSelector.disableElement();
        }
    }

    async onNextClick(evt) {
        let invalidSelector = null;

        if (!this.selectedMethodValue) {
            this.methodSelector.setInvalid();
            invalidSelector = this.methodSelector;
        }

        if (!this.selectedAddressValue) {
            this.addressSelector.setInvalid();
            invalidSelector = this.addressSelector;
        }

        if (invalidSelector) {
            invalidSelector.focus();
            evt.preventDefault();
        }
    }

    async onAddressUpdate(value) {
        this.prevAddressValue = this.selectedAddressValue;
        this.selectedAddressValue = value;

        if (!this.disableServiceUpdate) {
            this.resetMethods();

            this.setAddressLoadingState();
            await this.selectAddress();
            this.setAddressLoadingState(false);
        }
    }

    onAddressFormCancel() {
        this.updateUiOnly(() => {
            if (!this.#isEdit) {
                this.addressOptions = this.addressOptions.filter(({ value }) => value !== NEW_ADDRESS_OPTION_VALUE);
                this.addressSelector.setValue(this.prevAddressValue);
            }

            this.addressSelector.enable();
            this.addressSelector.enableElement();
        });
        this.$editCtrl.prop('disabled', false);
        this.$addCtrl.prop('disabled', false);
        this.formVisible = false;
    }

    onLoadingChange(loading) {
        this.nextLoader.active = loading;

        if (loading) {
            this.$addCtrl.addClass(CLASS_NAME_ADD_BUTTON_DISABLED).attr('aria-disabled', true);
            this.$editCtrl.addClass(CLASS_NAME_EDIT_BUTTON_DISABLED).attr('aria-disabled', true);
            this.$nextCtrl.addClass(CLASS_NAME_NEXT_BUTTON_DISABLED).attr('aria-disabled', true);
        } else {
            this.$addCtrl.removeClass(CLASS_NAME_EDIT_BUTTON_DISABLED).removeAttr('aria-disabled');
            this.$editCtrl.removeClass(CLASS_NAME_EDIT_BUTTON_DISABLED).removeAttr('aria-disabled');
            if(this.selectedMethodValue){
                this.$nextCtrl.removeClass(CLASS_NAME_NEXT_BUTTON_DISABLED).removeAttr('aria-disabled');
            }
        }
    }

    onMethodUpdate(value) {
        this.selectedMethodValue = value;

        this.postShippingMethod();
    }

    async onCheckoutFormUpdate(address) {
        if (!address) return;

        this.checkoutForm.hideError();
        this.resetMethods();

        const newAddressFields = {
            IsPrimary: false,
            CountryCode: "US",
            Country: "United States",
            PhoneNumber: null
        };

        const updatedAddress = {
            ...address,
            ...(this.isNewAddress ? newAddressFields : {})
        };

        this.checkoutForm.loading = true;
        this.setAddressLoadingState();

        await this.addOrEditAddress(updatedAddress);

        this.checkoutForm.loading = false;
        this.setAddressLoadingState(false);
    }

    parseAddressText(address) {
        let text = address.Address1;

        if (address.Address2) {
            text += ` ${address.Address2}`;
        }

        text += ` ${address.City}, ${address.StateCode} ${address.ZipPostalCode}`;

        return text;
    }

    renderLabels() {
        this.$addLabel.html(this.context.AddNew);
        this.$addressLabel.html(this.context.ShippingAddress);
        this.$editLabel.html(this.context.EditExistingAddress);
        this.$methodLabel.html(this.context.ShippingMethod);
        this.$nextLabel.html(this.context.Next);

        if(this.context.PromotionNoteIcon && this.context.PromotionNote){
            let pnote = `<img src="${this.context.PromotionNoteIcon}" /><div>${this.context.PromotionNote}</div>`;
            this.$promotionsnote.html(pnote);
            this.$promotionsnote.addClass("mt-3");
        }else{
            this.$promotionsnote.hide();
        }
    }

    renderCheckoutForm() {
        const defaultSchema = { ...addressFormDefaults.schema }
        const schema = {
            firstName: {
                ...defaultSchema.firstName,
                label: this.context.FirstName,
                name: 'FirstName'
            },
            lastName: {
                ...defaultSchema.lastName,
                label: this.context.LastName,
                name: 'LastName'
            },
            address: {
                ...defaultSchema.address,
                label: this.context.Address,
                name: 'Address1'
            },
            address2: {
                ...defaultSchema.address2,
                label: this.context.Address2,
                name: 'Address2'
            },
            city: {
                ...defaultSchema.city,
                label: this.context.City,
                name: 'City'
            },
            state: {
                ...defaultSchema.state,
                label: this.context.State,
                name: 'StateCode'
            },
            zip: {
                ...defaultSchema.zip,
                label: this.context.Zip,
                name: 'ZipPostalCode'
            }
        };

        const options = {
            classList: this.checkoutFormClassList,
            onCancelHandler: () => this.onAddressFormCancel(),
            replaceOriginEl: true,
            schema
        };

        const context = {
            cancel: this.context.Cancel,
            heading: this.context.AddShippingAddress,
            requiredToken: this.context.RequiredToken,
            submit: this.context.ApplyUpdates,
            validation: this.context.ValidationMessage
        };

        this.checkoutForm = new CheckoutAddressForm(this.$formPlaceholder, options, context);
    }

    renderNextCtrl() {
        if (this.$nextCtrl.is('a')) {
            this.$nextCtrl.attr('href', this.context.NextStepUrl);
            this.$nextCtrl.addClass(CLASS_NAME_NEXT_BUTTON_DISABLED).attr('aria-disabled', true);
        }
    }

    renderLoaders() {
        this.addressLoader = new AjaxLoader(this.$addressLoaderPlaceholder, {
            ...DEFAULT_LOADER_OPTIONS,
            srText: this.context.LoadingAddresses
        });

        this.methodLoader = new AjaxLoader(this.$methodLoaderPlaceholder, {
            ...DEFAULT_LOADER_OPTIONS,
            srText: this.context.LoadingMethods
        });

        this.nextLoader = new AjaxLoader(this.$nextLoaderPlaceholder, {
            aria: false,
            size: 'xs'
        });

        this.nextLoader.render();
    }

    renderSelectors() {
        this.addressSelector = new SelectBox(this.$addressPlaceholder, {
            id: this.addressId
        });
        this.addressSelector.disable();
        this.addressSelector.render();

        this.methodSelector = new SelectBox(this.$methodPlaceholder, {
            id: this.methodId
        });
        this.methodSelector.disable();
        this.methodSelector.render();
    }

    displayLegalDisclaimer() {
        let displayRxRequired = false;

        store.getters.lines.forEach(line => {
            if (line.rxRequired) displayRxRequired = true;
        });

        displayRxRequired ? this.$prescription.show() : this.$prescription.hide();;
    }

    setInitialAddress() {
        if (this.addressOptions.find(({ value }) => value === store.getters.shippingPartyId) && !this.$isSubscriptionsPage) {
            this.addressSelector.setValue(store.getters.shippingPartyId);
            return;
        }

        if (this.addressOptions.length > 0) {
            this.addressSelector.setValue(this.addressOptions[0].value);
        } else {
            this.$edit.hide();
            this.onAddAddressClick();
        }
    }

    setInitialMethod() {
        if (this.methodOptions.find(({ value }) => value === store.getters.shippingMethodId)) {
            this.methodSelector.setValue(store.getters.shippingMethodId);
        }
    }

    setupAccessibleCollapse() {
        const formId = `addressForm_${this.id}`;
        this.$form.attr('id', formId);
        this.$editCtrl.attr({ 'aria-expanded': false, 'aria-controls': formId });
        this.$addCtrl.attr({ 'aria-expanded': false, 'aria-controls': formId });
    }

    setupAccessibleIds() {
        const addressLabelId = `${this.addressId}_label`;
        const methodLabelId = `${this.methodId}_label`;

        this.$address.attr({ 'for': this.addressId, 'id': addressLabelId });
        this.$method.attr({ 'for': this.methodId, 'id': methodLabelId });

        this.$controls.attr('aria-labelledby', addressLabelId);
    }

    setupAccessibility() {
        this.setupAccessibleIds();
        this.setupAccessibleCollapse();

        this.$el.attr('aria-live', 'polite');
    }

    setupListeners() {
        this.$addCtrl.on('click', () => this.onAddAddressClick());
        this.$editCtrl.on('click', () => this.onEditClick());
        this.$nextCtrl.on('click', (evt) => this.onNextClick(evt));

        this.addressSelector.watchValue((value) => this.onAddressUpdate(value));
        this.methodSelector.watchValue((value) => this.onMethodUpdate(value));

        this.checkoutForm.watchValue((value) => this.onCheckoutFormUpdate(value));

        store.watch('loading', (...[, , loading]) => this.onLoadingChange(loading));
    }

    updateSummary(data) {
        store.mutations.SET_FREE_SHIPPING_TEXT(data.FreeShippingText);
        store.mutations.SET_PROMO_CODE(data.PromotionCode);

        store.actions.setFormattedCartSummary({
            miniCartTotal: data?.FormattedTotal?.FormattedMiniCartTotal,
            discount: data?.FormattedTotal?.FormattedDiscount,
            shippingTotal: data?.FormattedTotal?.FormattedShipping,
            subtotal: data?.FormattedTotal?.FormattedSubtotal,
            taxTotal: data?.FormattedTotal?.FormattedTax,
            total: data?.FormattedTotal?.FormattedGrandTotal
        });

        store.mutations.SET_LOADING(false);
        store.mutations.SET_LOADED(true);
    }

    updateUiOnly(callback) {
        if (!isFn(callback)) return;

        this.disableServiceUpdate = true;
        callback();
        this.disableServiceUpdate = false;
    }

    parseTemplate(templateStr = template) {
        const parsed = $.parseHTML(templateStr);
        return $(parsed);
    }

    static extendTemplate(templateStr) {
        class CheckoutShippingExtended extends this { };
        CheckoutShippingExtended.prototype.parseTemplate = CheckoutShipping.prototype.parseTemplate.bind(CheckoutShippingExtended, templateStr);
        return CheckoutShippingExtended;
    }
}

$(() => {
    const rootEls = document.querySelectorAll('.js-checkout-shipping');
    
    if(rootEls.length) {
        store.actions.getCart();
    }

    rootEls.forEach((el) => {
        const $el = $(el);
        const renderingContext = parseRenderingContext(el);

        const shipping = new CheckoutShipping($el, renderingContext);
        shipping.init();

        const unwatchLoaded = store.watch('loaded', (...[,,loaded]) => {
            if(loaded) {
                shipping.onCartLoaded();
                unwatchLoaded();
            }
        });
    });
});
